import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { useOutletContext, useNavigate } from "react-router";
import { Search, Loader2 } from "lucide-react";
import { href } from "../util";
import { searchMessages } from "../lib/xmpp/factories";
import type { MessageMatch, RawClient } from "../lib/xmpp";

type SearchResult = MessageMatch;

function useDebounce(value: string, delay: number): string {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return debounced;
}

function highlightMatch(text: string, query: string) {
	if (!query) return <>{text}</>;
	const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const parts = text.split(new RegExp(`(${escaped})`, "gi"));
	return (
		<>
			{parts.map((part, i) =>
				part.toLowerCase() === query.toLowerCase() ? (
					<mark key={i} className="bg-primary/30 text-white rounded px-0.5">
						{part}
					</mark>
				) : (
					<Fragment key={i}>{part}</Fragment>
				),
			)}
		</>
	);
}

function formatTimestamp(ts: string | number | Date | undefined): string {
	if (!ts) return "";
	const date = ts instanceof Date ? ts : new Date(ts);
	const now = new Date();
	const isToday =
		date.getDate() === now.getDate() &&
		date.getMonth() === now.getMonth() &&
		date.getFullYear() === now.getFullYear();
	if (isToday) {
		return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
	}
	return date.toLocaleDateString([], { month: "short", day: "numeric" }) +
		" " +
		date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export const SearchMessages = () => {
	const [query, setQuery] = useState("");
	const debouncedQuery = useDebounce(query, 300);
	const [results, setResults] = useState<SearchResult[]>([]);
	const [searching, setSearching] = useState(false);
	const [searched, setSearched] = useState(false);
	const navigate = useNavigate();
	const abortRef = useRef(0);

	const { client, accountId } = useOutletContext<{
		client: RawClient;
		accountId: string;
	}>();

	const performSearch = useCallback(
		async (q: string) => {
			if (!q.trim() || !client) {
				setResults([]);
				setSearched(false);
				return;
			}

			const searchId = ++abortRef.current;
			setSearching(true);
			setSearched(false);

			try {
				const found = await searchMessages(client, q);
				if (searchId !== abortRef.current) return;
				setResults(found);
			} catch (err) {
				console.error("Search failed:", err);
				if (searchId === abortRef.current) setResults([]);
			} finally {
				if (searchId === abortRef.current) {
					setSearching(false);
					setSearched(true);
				}
			}
		},
		[client],
	);

	useEffect(() => {
		performSearch(debouncedQuery);
	}, [debouncedQuery, performSearch]);

	const handleResultClick = (result: SearchResult) => {
		const messageAnchor = result.message.serverId || result.message.localId;
		const path = href("/accounts/:accountId/chats/:chatId", {
			accountId,
			chatId: result.chatId,
		});
		navigate(path + (messageAnchor ? `#message:${messageAnchor}` : ""));
	};

	// Group results by chat
	const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
		(acc[r.chatId] ??= []).push(r);
		return acc;
	}, {});

	return (
		<div className="flex flex-col h-full p-6 max-w-2xl mx-auto">
			<h1 className="text-xl font-semibold text-white mb-4">Search Messages</h1>

			<div className="relative mb-6">
				<Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
				<input
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search messages..."
					autoFocus
					className="w-full pl-9 pr-4 py-3 rounded-xl glass-panel border-white/8 text-white placeholder-white/40 text-sm focus:outline-none focus:border-white/20 transition-colors"
				/>
				{searching && (
					<Loader2
						size={16}
						className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 animate-spin"
					/>
				)}
			</div>

			{searched && results.length === 0 && debouncedQuery.trim() && (
				<div className="glass-panel rounded-xl p-4 text-white/50 text-sm text-center">
					No messages found for &ldquo;{debouncedQuery}&rdquo;
				</div>
			)}

			{Object.keys(grouped).length > 0 && (
				<div className="flex flex-col gap-1 overflow-y-auto">
					{Object.entries(grouped).map(([chatId, chatResults]) => (
						<div key={chatId}>
							<p className="text-xs font-medium text-white/30 uppercase tracking-wider px-4 py-2">
								{chatResults[0].chatName}
							</p>
							{chatResults.map((result, i) => (
								<button
									key={result.message.serverId || result.message.localId || i}
									onClick={() => handleResultClick(result)}
									className="w-full text-left px-4 py-3 hover:bg-white/5 rounded-xl transition-colors"
								>
									<p className="text-sm font-medium text-white">
										{result.chatName}
									</p>
									<p className="text-xs text-white/50 mt-0.5 line-clamp-2">
										{highlightMatch(result.message.text, debouncedQuery)}
									</p>
									<p className="text-xs text-white/30 mt-1">
										{formatTimestamp(result.message.timestamp)}
									</p>
								</button>
							))}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
